import React, { createContext, useContext, useState, useEffect } from 'react'
import { Session, User } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'
import { AuthContextType, Organization, OrganizationMember } from './types'
import * as authFns from './authFunctions'
import * as orgFns from './organizationFunctions'
import { setOrganizationIdCache } from '@/lib/supabase/products/organization'

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  const [currentOrganization, setCurrentOrganization] = useState<Organization | null>(null)

  useEffect(() => {
    const fetchSession = async () => {
      console.log('Récupération de la session...')
      const { data: { session }, error } = await supabase.auth.getSession()
      
      if (error) {
        console.error('Erreur lors de la récupération de la session:', error)
      } else {
        console.log('Session récupérée:', session)
      }
      
      setSession(session)
      setUser(session?.user ?? null)
      setLoading(false)
      
      if (session?.user) {
        fetchAndSetOrganization(session.user.id)
      }
    }

    fetchSession()

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, newSession) => {
        console.log('État d\'authentification changé:', event)
        if (newSession) {
          console.log('Nouvel utilisateur:', newSession.user)
          
          if (event === 'SIGNED_IN' && newSession.user) {
            fetchAndSetOrganization(newSession.user.id)
          }
        } else if (event === 'SIGNED_OUT') {
          localStorage.removeItem('currentOrganizationId')
          setCurrentOrganization(null)
          setOrganizationIdCache(null)
        }
        
        setSession(newSession)
        setUser(newSession?.user ?? null)
        setLoading(false)
      }
    )

    return () => {
      subscription.unsubscribe()
    }
  }, [])
  
  const fetchAndSetOrganization = async (userId: string) => {
    try {
      const org = await orgFns.getCurrentOrganization(userId)
      setCurrentOrganization(org)
      
      if (org) {
        console.log('Organization récupérée et stockée:', org.id)
        localStorage.setItem('currentOrganizationId', org.id)
        setOrganizationIdCache(org.id)
      }
    } catch (error) {
      console.error('Erreur lors de la récupération de l\'organisation:', error)
    }
  }

  const signUp = async (email: string, password: string, metadata?: any) => {
    setLoading(true)
    try {
      const result = await authFns.signUp(email, password, metadata)
      return result
    } finally {
      setLoading(false)
    }
  }

  const signIn = async (email: string, password: string) => {
    setLoading(true)
    try {
      const result = await authFns.signIn(email, password)
      return result
    } finally {
      setLoading(false)
    }
  }

  const signInWithGoogle = async () => {
    setLoading(true)
    try {
      const result = await authFns.signInWithGoogle()
      return result
    } finally {
      setLoading(false)
    }
  }

  const resetPassword = async (email: string) => {
    setLoading(true)
    try {
      const result = await authFns.resetPassword(email)
      return result
    } finally {
      setLoading(false)
    }
  }

  const updatePassword = async (password: string) => {
    setLoading(true)
    try {
      const result = await authFns.updatePassword(password)
      return result
    } finally {
      setLoading(false)
    }
  }

  const signOut = async () => {
    setLoading(true)
    try {
      const result = await authFns.signOut()
      setUser(null)
      setSession(null)
      return result
    } finally {
      setLoading(false)
    }
  }

  const createOrganization = async (name: string, plan: string = 'standard') => {
    if (!user) {
      throw new Error('Utilisateur non connecté')
    }
    return orgFns.createOrganization(user.id, name, plan)
  }

  const getCurrentOrganization = async (): Promise<Organization | null> => {
    if (!user) return null
    return orgFns.getCurrentOrganization(user.id)
  }

  const getOrganizationMembers = async (organizationId: string): Promise<OrganizationMember[]> => {
    return orgFns.getOrganizationMembers(organizationId)
  }

  const inviteToOrganization = async (organizationId: string, email: string, role: string = 'member') => {
    if (!user) {
      throw new Error('Utilisateur non connecté')
    }
    return orgFns.inviteToOrganization(user.id, organizationId, email, role)
  }

  return (
    <AuthContext.Provider
      value={{
        session,
        user,
        currentOrganization,
        signUp,
        signIn,
        signInWithGoogle,
        signOut,
        resetPassword,
        updatePassword,
        loading,
        createOrganization,
        getCurrentOrganization,
        getOrganizationMembers,
        inviteToOrganization
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
